import React, { useState, useEffect } from 'react';
import {
    Button, Dialog, DialogTitle, DialogContent, Tabs, Tab, Select,
    MenuItem, FormControl, InputLabel, IconButton, Stack, Box, TextField, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingOverlay from "./loadingOverlay";
import moment from 'moment';

// API
import { exportCSV, SMexportCSV, export_custom_pdf } from '../api/others';
import { fetchSiteDetails } from './../api/plant';
import { storing_values, reading_values, } from '../api/bill';

const ExportButton = ({ siteID, userType, EnergySupplier }) => {
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [csvInterval, setCsvInterval] = useState('300');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [pdfDate, setPdfDate] = useState(new Date().toISOString().slice(0, 7));
    const [includeBI, setIncludeBill] = useState(true);
    const [includeAnalysis, setIncludeAnalysis] = useState(false);
    const [includeMonthly, setIncludeMonthly] = useState(false);
    const [customPdfError, setCustomPdfError] = useState('');
    const [includeCarbonReporting, setIncludeCarbonReporting] = useState(false);

    const [afa, setAFA] = useState("0.03");
    const [nem, setNem] = useState("0.2");

    const [isLoading, setIsLoading] = useState(false);
    const [siteDetails, setSiteDetails] = useState(null);
    const [isFetchingValues, setIsFetchingValues] = useState(false);

    useEffect(() => {
        if (siteID) {
            fetchSiteDetails(siteID)
                .then(data => setSiteDetails(data))
                .catch(err => console.error('Failed to fetch site details:', err));
        }
    }, [siteID]);

    // Fetch stored values when Custom PDF tab is active
    useEffect(() => {
        const fetchStoredValues = async () => {
            if (open && activeTab === 1 && siteID && pdfDate) {
                setIsFetchingValues(true);
                try {
                    const formattedDate = moment(pdfDate).format("YYYY-MM");
                    const { data: readingData } = await reading_values(siteID, formattedDate);

                    if (readingData?.afa !== null && readingData?.afa !== undefined) {
                        setAFA(readingData.afa.toString());
                    }
                    if (readingData?.nem !== null && readingData?.nem !== undefined) {
                        setNem(readingData.nem.toString());
                    }
                } catch (err) {
                    console.error("Error fetching stored values:", err);
                } finally {
                    setIsFetchingValues(false);
                }
            }
        };

        fetchStoredValues();
    }, [open, activeTab, siteID, pdfDate]);

    const handleAFAChange = async (e) => {
        const value = e.target.value;
        if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
            setAFA(value);
            if (value !== "" && siteID && pdfDate) {
                try {
                    const formattedDate = moment(pdfDate).format("YYYY-MM");
                    await storing_values(siteID, formattedDate, Number(value), Number(nem || "0.2"));
                } catch (err) {
                    console.error("Error storing AFA value:", err);
                }
            }
        }
    };

    const handleNemChange = async (e) => {
        const value = e.target.value;
        if (value === "" || /^\d*\.?\d*$/.test(value)) {
            setNem(value);
            if (value !== "" && siteID && pdfDate) {
                try {
                    const formattedDate = moment(pdfDate).format("YYYY-MM");
                    await storing_values(siteID, formattedDate, Number(afa || "0.03"), Number(value));
                } catch (err) {
                    console.error("Error storing NEM value:", err);
                }
            }
        }
    };

    const handleAFABlur = async (e) => {
        const value = e.target.value;
        const finalValue = (value === "" || value === "-" || value === "." || value === "-.")
            ? "0.03"
            : (isNaN(parseFloat(value)) ? "0.03" : parseFloat(value).toString());
        setAFA(finalValue);
        if (siteID && pdfDate) {
            try {
                const formattedDate = moment(pdfDate).format("YYYY-MM");
                await storing_values(siteID, formattedDate, Number(finalValue), Number(nem || "0.2"));
            } catch (err) {
                console.error("Error storing AFA value on blur:", err);
            }
        }
    };

    const handleNemBlur = async (e) => {
        const value = e.target.value;
        const finalValue = (value === "" || value === ".")
            ? "0.2"
            : (isNaN(parseFloat(value)) ? "0.2" : parseFloat(value).toString());
        setNem(finalValue);
        if (siteID && pdfDate) {
            try {
                const formattedDate = moment(pdfDate).format("YYYY-MM");
                await storing_values(siteID, formattedDate, Number(afa || "0.03"), Number(finalValue));
            } catch (err) {
                console.error("Error storing NEM value on blur:", err);
            }
        }
    };

    const handleCSVSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const formattedStart = new Date(startDate).toISOString().split('T')[0];
            const formattedEnd = new Date(endDate).toISOString().split('T')[0];
            if (userType === 1) {
                await SMexportCSV(formattedStart, formattedEnd, csvInterval, siteID);
            } else {
                await exportCSV(formattedStart, formattedEnd, csvInterval, siteID);
            }
        } finally {
            setIsLoading(false);
            setOpen(false);
        }
    };

    const handleCustomPDFSubmit = async (e) => {
        e.preventDefault();
        if (!includeBI && !includeAnalysis && !includeMonthly && !includeCarbonReporting) {
            setCustomPdfError('Please select at least one option.');
            return;
        }
        setCustomPdfError('');
        setIsLoading(true);
        try {
            const formattedDate = moment(pdfDate).format("YYYY-MM");
            if (siteID && pdfDate) {
                await storing_values(siteID, formattedDate, Number(afa || "0.03"), Number(nem || "0.2"));
            }
            await export_custom_pdf(
                siteID, formattedDate,
                includeBI, includeAnalysis, includeMonthly, includeCarbonReporting,
                afa || "0.03", nem || "0.2",
            );
        } catch (err) {
            console.error('Custom PDF export failed:', err);
        } finally {
            setIsLoading(false);
            setOpen(false);
        }
    };

    const allMeters = siteDetails?.smartmeters || [];
    const allDates = allMeters.map(m => new Date(m.sm_installation_date)).filter(d => !isNaN(d));
    const earliestDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : undefined;
    const earliestMonth = earliestDate ? earliestDate.toISOString().slice(0, 7) : undefined;

    return (
        <Box>
            <Button
                variant="contained"
                onClick={() => setOpen(true)}
                sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#115293' } }}
            >
                Export
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', width: '400px'
                }}>
                    Export Data
                    <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>

                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    centered
                    sx={{ borderBottom: '1px solid #e0e0e0', '& .MuiTabs-indicator': { backgroundColor: '#1976d2' } }}
                >
                    <Tab label="CSV Export" sx={{ fontWeight: 600, '&.Mui-selected': { color: '#1976d2' } }} />
                    <Tab label="PDF Export" sx={{ fontWeight: 600, '&.Mui-selected': { color: '#1976d2' } }} />
                </Tabs>

                <DialogContent sx={{ padding: 3 }}>
                    {activeTab === 0 ? (
                        <Box component="form" onSubmit={handleCSVSubmit}>
                            <Stack spacing={3} sx={{ marginTop: 2 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Interval</InputLabel>
                                    <Select
                                        value={csvInterval}
                                        label="Interval"
                                        onChange={(e) => setCsvInterval(e.target.value)}
                                    >
                                        <MenuItem value="300">5 Minutes</MenuItem>
                                        <MenuItem value="900">15 Minutes</MenuItem>
                                        <MenuItem value="1800">30 Minutes</MenuItem>
                                        <MenuItem value="3600">60 Minutes</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="Start Date" type="date" value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    fullWidth InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: earliestDate ? earliestDate.toISOString().split("T")[0] : undefined,
                                        max: new Date().toISOString().split("T")[0],
                                    }}
                                />

                                <TextField
                                    label="End Date" type="date" value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    fullWidth InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: startDate,
                                        max: new Date().toISOString().split("T")[0],
                                    }}
                                />

                                <Box sx={{ display: "flex", height: "auto", maxHeight: "10vh", position: "relative", justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                                    {isLoading && <LoadingOverlay sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />}
                                </Box>

                                <Button type="submit" variant="contained"
                                    sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}>
                                    Export CSV
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleCustomPDFSubmit}>
                            <Stack spacing={3} sx={{ marginTop: 2 }}>
                                <TextField
                                    label="Month/Year" type="month" value={pdfDate}
                                    onChange={(e) => setPdfDate(e.target.value)}
                                    fullWidth InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: earliestMonth, max: new Date().toISOString().slice(0, 7) }}
                                    disabled={!siteDetails}
                                />

                                {isFetchingValues && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Loading values...</Typography>
                                    </Box>
                                )}

                                {EnergySupplier === "NUR" ? (
                                    <TextField label="ICPT (RM/kWh)" type="text" value={afa}
                                        onChange={handleAFAChange} onBlur={handleAFABlur}
                                        fullWidth helperText="Value will be automatically saved" />
                                ) : (
                                    <TextField label="AFA (RM/kWh)" type="text" value={afa}
                                        onChange={handleAFAChange} onBlur={handleAFABlur}
                                        fullWidth helperText="Value will be automatically saved" />
                                )}

                                <TextField label="NEM" type="text" value={nem}
                                    onChange={handleNemChange} onBlur={handleNemBlur}
                                    fullWidth helperText="Value will be automatically saved" />

                                <Typography variant="body1" fontWeight={600}>Select sections to include:</Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {[
                                        { id: 'include_bill', checked: includeBI, setter: setIncludeBill, label: 'Bill Summary' },
                                        { id: 'include_analysis', checked: includeAnalysis, setter: setIncludeAnalysis, label: 'Analysis Report' },
                                        { id: 'include_monthly', checked: includeMonthly, setter: setIncludeMonthly, label: 'Monthly Report' },
                                        { id: 'include_carbon', checked: includeCarbonReporting, setter: setIncludeCarbonReporting, label: 'Carbon Reporting' },
                                    ].map(({ id, checked, setter, label }) => (
                                        <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <input type="checkbox" id={id} checked={checked}
                                                onChange={(e) => setter(e.target.checked)}
                                                style={{ width: 18, height: 18, cursor: 'pointer' }} />
                                            <label htmlFor={id} style={{ cursor: 'pointer', fontSize: '1rem' }}>{label}</label>
                                        </Box>
                                    ))}
                                </Box>

                                {customPdfError && (
                                    <Typography variant="body2" color="error">{customPdfError}</Typography>
                                )}

                                <Box sx={{ display: "flex", height: "auto", maxHeight: "10vh", position: "relative", justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                                    {isLoading && <LoadingOverlay sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />}
                                </Box>

                                <Button type="submit" variant="contained"
                                    sx={{ backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#4a148c' } }}>
                                    Export PDF
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ExportButton;